import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {Alert, Autocomplete, Checkbox, CircularProgress, TextField} from "@mui/material";
import {generatePath, useNavigate, useParams} from "react-router";
import {useSitStores} from "../../../../stores";
import dayjs from "dayjs";
import type {Category, Cluster, Company, Division} from "../../../ApiTypes";
import {category_readable_str} from "../../../ApiTypes";
import {sitRoutes} from "../../../routing/sitRoutes";
import {Category as CategoryIcon, Check, ChevronLeft, History, InfoOutlined, Payments} from "@mui/icons-material";
import {SitUserSelect} from "../../../components/SitUserSelect";
import {SitField, SitSection, sitFormTags} from "../../../components/design/SitDesignAtoms";
import {sitFieldLabel} from "~/sit/sitLabels.ts";
import {roundAmount} from "~/utils/js-utils";
import {TargetAuditLogForm} from "./targetAuditLogForm";

// Text money inputs: parse to float and round so the stored value (and the value
// sent to the backend) is exactly what the user sees, free of float noise.
const num = (e: React.ChangeEvent<HTMLInputElement>) => roundAmount(parseFloat(e.target.value));

// Cap a multi-select value to a single (last) item when the config flag is set.
function cap<T>(value: T[], single?: boolean): T[] {
    return single ? value.slice(-1) : value;
}

export const TargetForm: React.FC = observer(() => {
    const {ccStore, sitApi, targetListStore, targetInstanceStore: s} = useSitStores();
    const navigate = useNavigate();
    const params = useParams<{targetId: string}>();
    const targetId: number | 'new' = 'targetId' in params ? parseInt(params['targetId'] ?? '') || 'new' : 'new';

    const [isLoading, setIsLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');

    // Prefix the unit with "K" when amounts are shown in thousands (see SitConfiguration).
    const ccy = ccStore.getCurrencyCode(s.instance.currency_id ?? 0, ccStore.valuesInThousands ? 'K ' : '');
    const kHelp = ccStore.valuesInThousands ? 'Value in thousands (k).' : undefined;
    const noRate = ccStore.convertToCurrency(1, s.instance.currency_id ?? 0, s.profileStore.currencySymbol ?? '') === 0;

    // Scope selectors can be limited to a single value via SitConfiguration.
    const cfg = ccStore.sitConfigRM.result;

    useEffect(() => {
        if (targetId === 'new')
            s.setInstance({});
        else
            sitApi.getTarget(targetId).then((resp) => {
                s.setInstance(resp.data);
            }).catch(() => {
                navigate(generatePath(sitRoutes.sit_targets));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId]);

    const goBack = () => {
        s.setInstance({});
        navigate(generatePath(sitRoutes.sit_targets));
    };

    return (
        <div className="sit-formscreen">
            <form onSubmit={(event) => {
                event.preventDefault();
                setIsLoading(true);
                s.createOrUpdate()
                    .then(() => {
                        s.setInstance({});
                        targetListStore.reset();
                        targetListStore.reloadData();
                        navigate(generatePath(sitRoutes.sit_targets));
                    })
                    .catch(error => {
                        console.error(error);
                        setErrorMessage('Could not add target');
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }}>
                <div className="sit-form-head">
                    <button type="button" className="ac-icon-btn" title="Back to targets" onClick={goBack}>
                        <ChevronLeft/>
                    </button>
                    <div className="sit-form-head-mid">
                        <div className="sit-form-eyebrow">
                            {targetId === 'new' ? "New target · Target details" : `TGT-${targetId} · Target details`}
                        </div>
                        <input className="sit-form-titleinput" placeholder="Untitled target"
                               value={s.instance?.title ?? ''}
                               onChange={e => s.title = e.target.value}/>
                    </div>
                    <button type="button" className="ac-btn" onClick={goBack}>Cancel</button>
                    <button type="submit" className="ac-btn ac-btn--primary"
                            disabled={isLoading ||
                                (cfg?.single_division && s.divisionValues.length > 1) ||
                                (cfg?.single_category && s.categoryValues.length > 1) ||
                                (cfg?.single_cluster && s.combinedClusters.length > 1) ||
                                (cfg?.single_company && s.companyValues.length > 1)}>
                        {isLoading ? <CircularProgress size={14}/> : <Check fontSize="small"/>}
                        {targetId === 'new' ? 'Add target' : 'Update target'}
                    </button>
                </div>

                <div className="sit-form-scroll">
                    <div className="sit-form-col">
                        <div className="sit-details">
                            <SitSection icon={<InfoOutlined/>} title="General information" tint="a">
                                <div className="sit-tgt-row2">
                                    <SitField label={sitFieldLabel('title')} req full>
                                        <input value={s.instance.title ?? ''} required
                                               onChange={e => s.title = e.target.value}/>
                                    </SitField>
                                    <SitUserSelect
                                        label="Owner"
                                        placeholder="Select owner"
                                        disabled={false}
                                        required
                                        value={s.instance.owner}
                                        onChange={(_event, value) => {
                                            if (value !== null) s.owner = value;
                                        }}/>
                                    <SitUserSelect
                                        label="Approver"
                                        placeholder="Select approver"
                                        disabled={false}
                                        required
                                        value={s.instance.approver}
                                        onChange={(_event, value) => {
                                            if (value !== null) s.approver = value;
                                        }}/>
                                </div>
                            </SitSection>

                            <SitSection icon={<Payments/>} title="Financials" tint="c">
                                <div className="sit-tgt-fin">
                                    <SitField label="Target year">
                                        <input type="number" min={2000} max={2100}
                                               value={s.instance.target_year ?? ''}
                                               onChange={e => {
                                                   const y = parseInt(e.target.value);
                                                   if (y) s.targetYear = dayjs(new Date(y, 0, 1));
                                               }}/>
                                    </SitField>
                                    <SitField label="Currency" req>
                                        <select required value={s.instance.currency_id ?? ''}
                                                onChange={e => {
                                                    if (e.target.value) s.currencyId = Number(e.target.value);
                                                }}>
                                            <option value="">—</option>
                                            {ccStore.currencyRM.result?.map(c => <option key={c.id}
                                                                                         value={c.id}>{c.code}</option>)}
                                        </select>
                                    </SitField>
                                    <SitField label="Baseline spend" units={ccy} help={kHelp}>
                                        <input type="text" inputMode="decimal" value={s.instance.baseline_spend ?? 0}
                                               onChange={e => s.baselineSpend = num(e)}/>
                                    </SitField>
                                    <SitField label="Total cost saving" units={ccy} help={kHelp}>
                                        <input type="text" inputMode="decimal" value={s.instance.saving_amount ?? 0}
                                               onChange={e => s.savingAmount = num(e)}/>
                                    </SitField>
                                    <SitField label="Total cost avoidance" units={ccy} help={kHelp}>
                                        <input type="text" inputMode="decimal" value={s.instance.avoidance_amount ?? 0}
                                               onChange={e => s.avoidanceAmount = num(e)}/>
                                    </SitField>
                                </div>
                                {noRate && <Alert severity="error" color="error" className="mt-2">
                                    Conversion rate not found! Add a rate for the selected currency pair in settings.
                                </Alert>}
                            </SitSection>

                            <SitSection icon={<CategoryIcon/>} title="Scope & classification" tint="b">
                                <div className="sit-tgt-fin">
                                    <SitField label="Scope">
                                        <select value={s.instance.local ? 1 : 2}
                                                onChange={e => s.local = Number(e.target.value) === 1}>
                                            <option value={1}>Local</option>
                                            <option value={2}>Global</option>
                                        </select>
                                    </SitField>

                                    <label className="sitf">
                                        <span className="sitf-label">Company</span>
                                        <Autocomplete
                                            multiple selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                                            size="small" limitTags={1}
                                            renderTags={sitFormTags((o: {title: string}) => o.title)}
                                            options={ccStore.companyRM.result ?? ([] as Company[])}
                                            getOptionLabel={(o) => o.title}
                                            value={s.companyValues}
                                            renderInput={(params) =>
                                                <TextField {...params} variant="outlined"
                                                           placeholder="Select company"/>}
                                            renderOption={(props, option) =>
                                                <li {...props} key={option.id}>
                                                    <Checkbox
                                                        checked={(s.instance.companies?.indexOf(option.id) ?? -1) > -1}/>
                                                    {option.title}
                                                </li>}
                                            onChange={(_e, value) => s.companies = cap(value, cfg?.single_company).map((c) => c.id)}/>
                                        {cfg?.single_company && s.companyValues.length > 1 &&
                                            <Alert severity="error" color="error" className="mt-1">
                                                Only one company is allowed!
                                            </Alert>}
                                    </label>

                                    <label className="sitf">
                                        <span className="sitf-label">Cluster</span>
                                        <Autocomplete
                                            multiple selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                                            size="small" limitTags={1}
                                            renderTags={sitFormTags((o: {title: string}) => o.title)}
                                            options={ccStore.clusterRM.result ?? ([] as Cluster[])}
                                            getOptionLabel={(o) => o.title}
                                            isOptionEqualToValue={(o, v) => o.id === v.id}
                                            value={s.combinedClusters}
                                            renderInput={(params) =>
                                                <TextField {...params} variant="outlined"
                                                           placeholder="Select cluster"/>}
                                            renderOption={(props, option) =>
                                                <li {...props} key={option.id}>
                                                    <Checkbox checked={s.isCheckedClusters(option.id)}
                                                              indeterminate={s.isIndeterminateCluster(option.id)}/>
                                                    {option.title}
                                                </li>}
                                            onChange={(_e, value, reason) => s.setClusters(cap(value, cfg?.single_cluster), reason === 'removeOption')}/>
                                        {cfg?.single_cluster && s.combinedClusters.length > 1 &&
                                            <Alert severity="error" color="error" className="mt-1">
                                                Only one cluster is allowed!
                                            </Alert>}
                                    </label>

                                    <label className="sitf">
                                        <span className="sitf-label">Division</span>
                                        <Autocomplete
                                            multiple selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                                            size="small" limitTags={1}
                                            renderTags={sitFormTags((o: {title: string}) => o.title)}
                                            options={ccStore.divisionRM.result ?? ([] as Division[])}
                                            getOptionLabel={(o) => o.title}
                                            value={s.divisionValues}
                                            renderInput={(params) =>
                                                <TextField {...params} variant="outlined"
                                                           placeholder="Select division"/>}
                                            renderOption={(props, option) =>
                                                <li {...props} key={option.id}>
                                                    <Checkbox
                                                        checked={(s.instance.divisions?.indexOf(option.id) ?? -1) > -1}/>
                                                    {option.title}
                                                </li>}
                                            onChange={(_e, value) => s.divisions = cap(value, cfg?.single_division).map((d) => d.id)}/>
                                        {cfg?.single_division && s.divisionValues.length > 1 &&
                                            <Alert severity="error" color="error" className="mt-1">
                                                Only one division is allowed!
                                            </Alert>}
                                    </label>

                                    <label className="sitf">
                                        <span className="sitf-label">Category</span>
                                        <Autocomplete
                                            multiple selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                                            size="small" limitTags={1} renderTags={sitFormTags(category_readable_str)}
                                            options={ccStore.categoryRM.result ?? ([] as Category[])}
                                            getOptionLabel={(o) => category_readable_str(o)}
                                            value={s.categoryValues}
                                            renderInput={(params) =>
                                                <TextField {...params} variant="outlined"
                                                           placeholder="Select category"/>}
                                            renderOption={(props, option) =>
                                                <li {...props} key={option.id}>
                                                    <Checkbox
                                                        checked={(s.instance.categories?.indexOf(option.id) ?? -1) > -1}/>
                                                    {category_readable_str(option)}
                                                </li>}
                                            onChange={(_e, value) => s.categories = cap(value, cfg?.single_category).map((c) => c.id)}/>
                                        {cfg?.single_category && s.categoryValues.length > 1 &&
                                            <Alert severity="error" color="error" className="mt-1">
                                                Only one category is allowed!
                                            </Alert>}
                                    </label>
                                </div>
                            </SitSection>

                            {targetId !== 'new' &&
                            <SitSection icon={<History/>} title="Activity log" tint="a"
                                        sub="Who changed what on this target, and when.">
                                <TargetAuditLogForm/>
                            </SitSection>}
                        </div>

                        {errorMessage &&
                            <Alert severity="error" className="mt-2">{errorMessage}</Alert>}
                    </div>
                </div>
            </form>
        </div>
    );
});
